import { TONE } from './constants'

export const RESTRICTED_GUEST_USER_ROLE_ID = '2af84b1e-32c8-42b7-82bc-daa82404023b'
export const USER_GUEST_USER_ROLE_ID = 'a0b1b346-4d3e-4e8b-98f8-753987be4970'
export const STANDARD_GUEST_USER_ROLE_ID = '10dae51f-b6af-4016-8d66-8c2a99b929b3'

const USER_SELF_CONSENT_POLICY_PREFIX = 'managepermissiongrantsforself.'
const DEFAULT_USER_PERMISSION_GRANT_POLICY_RECOMMENDATION =
  'Self-consent disabled, Microsoft recommended, or low-risk only'

// https://learn.microsoft.com/en-us/entra/identity/enterprise-apps/manage-app-consent-policies?pivots=ms-graph#app-consent-policy-segments
const PERMISSION_GRANT_POLICY_LABEL_BY_ID = {
  'microsoft-user-default-low': 'Low-risk user consent',
  'microsoft-user-default-recommended': 'Microsoft managed recommendation',
  'microsoft-user-default-allow-consent-apps': 'Popular mail client consent',
  'microsoft-user-default-legacy': 'Legacy broad user consent',
  'microsoft-all-application-permissions': 'All application permissions',
  'microsoft-dynamically-managed-permissions-for-chat': 'Chat dynamic permissions',
  'microsoft-all-application-permissions-for-chat': 'All chat permissions',
  'microsoft-dynamically-managed-permissions-for-team': 'Team dynamic permissions',
  'microsoft-pre-approval-apps-for-chat': 'Chat pre-approved apps',
  'microsoft-pre-approval-apps-for-team': 'Team pre-approved apps',
  'microsoft-all-application-permissions-verified': 'All permissions for verified apps',
  'microsoft-application-admin': 'Application administrator consent',
  'microsoft-company-admin': 'Company administrator consent',
}

export const isPresent = (value) => value !== undefined && value !== null

export const getUserSelfConsentPolicyAssignments = (value) => {
  if (!Array.isArray(value)) return []

  return value
    .map((rawPolicy) => ({ raw: String(rawPolicy), normalized: String(rawPolicy).toLowerCase() }))
    .filter(({ normalized }) => normalized.startsWith(USER_SELF_CONSENT_POLICY_PREFIX))
    .map(({ raw, normalized }) => {
      const policyId = normalized.replace(USER_SELF_CONSENT_POLICY_PREFIX, '')

      return {
        raw,
        policyId,
        label: PERMISSION_GRANT_POLICY_LABEL_BY_ID[policyId] ?? policyId,
      }
    })
}

export const buildTenantAuthzRows = (assessments = []) => {
  const assessmentByKey = Object.fromEntries(assessments.map((assessment) => [assessment.key, assessment]))

  return [
    {
      key: 'invites',
      sourceKey: 'allowInvitesFrom',
      labelId: 'AI_RISK_REPORT.SETTINGS.AUTHZ.USERS_PERMITTED_TO_INVITE_GUESTS',
      deviationTone: TONE.DANGER,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOW_INVITES_FROM',
    },
    {
      key: 'emailVerifiedJoin',
      sourceKey: 'allowEmailVerifiedUsersToJoinOrganization',
      labelId: 'AI_RISK_REPORT.SETTINGS.AUTHZ.EMAIL_VERIFIED_USERS_CAN_JOIN',
      deviationTone: TONE.DANGER,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOW_EMAIL_VERIFIED_USERS_TO_JOIN',
    },
    {
      key: 'emailBasedSubscriptions',
      sourceKey: 'allowedToSignUpEmailBasedSubscriptions',
      labelId: 'AI_RISK_REPORT.SETTINGS.AUTHZ.EMAIL_BASED_SUBSCRIPTIONS',
      deviationTone: TONE.DANGER,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOWED_TO_SIGN_UP_EMAIL_BASED_SUBSCRIPTIONS',
    },
    {
      key: 'riskyAppsConsent',
      sourceKey: 'allowUserConsentForRiskyApps',
      labelId: 'AI_RISK_REPORT.SETTINGS.AUTHZ.RISKY_APP_CONSENT',
      deviationTone: TONE.DANGER,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOW_USER_CONSENT_FOR_RISKY_APPS',
    },
    {
      key: 'apps',
      sourceKey: 'defaultUserRolePermissions.allowedToCreateApps',
      labelId: 'AI_RISK_REPORT.SETTINGS.AUTHZ.STANDARD_USERS_REGISTER_APPLICATIONS',
      deviationTone: TONE.WARN,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOWED_TO_CREATE_APPS',
    },
    {
      key: 'tenants',
      sourceKey: 'defaultUserRolePermissions.allowedToCreateTenants',
      labelId: 'AI_RISK_REPORT.SETTINGS.AUTHZ.STANDARD_USERS_CREATE_TENANTS',
      deviationTone: TONE.DANGER,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOWED_TO_CREATE_TENANTS',
    },
    {
      key: 'groups',
      sourceKey: 'defaultUserRolePermissions.allowedToCreateSecurityGroups',
      labelId: 'AI_RISK_REPORT.SETTINGS.AUTHZ.STANDARD_USERS_CREATE_SECURITY_GROUPS',
      deviationTone: TONE.WARN,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOWED_TO_CREATE_SECURITY_GROUPS',
    },
    {
      key: 'guestRole',
      sourceKey: 'guestUserRoleId',
      labelId: 'AI_RISK_REPORT.SETTINGS.AUTHZ.GUEST_USER_DIRECTORY_ROLE',
      deviationTone: TONE.DANGER,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.GUEST_USER_ROLE_ID',
    },
    {
      key: 'permissionGrantPolicy',
      sourceKey: 'defaultUserRolePermissions.permissionGrantPoliciesAssigned',
      labelId: 'AI_RISK_REPORT.SETTINGS.AUTHZ.DEFAULT_USER_PERMISSION_GRANT_POLICY',
      deviationTone: TONE.WARN,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.PERMISSION_GRANT_POLICY',
    },
  ].map((row) => {
    const assessment = assessmentByKey[row.key] ?? { current: null, recommended: null, result: null }
    let { recommended } = assessment
    if (row.key === 'guestRole' && recommended) recommended = `${recommended} (RestrictedGuest)`
    if (row.key === 'permissionGrantPolicy' && recommended === 'recommended_or_disabled') {
      recommended = DEFAULT_USER_PERMISSION_GRANT_POLICY_RECOMMENDATION
    }

    return { ...row, ...assessment, recommended }
  })
}
